import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { formatPrice } from '@/components/ui/Price';
import { computeUnitPrice } from '@/features/menu/computeUnitPrice';
import { minRequired, isEffectivelyRequired } from '@/features/menu/optionRequirement';
import type { ConsumerMode } from '@/features/menu/resolveModePrice';
import DishCodeTag from '@/features/menu/DishCodeTag';
import { inGroupOrder } from '@/features/menu/quickAddToCart';
import {
  selectWaiterCartLines,
  addWaiterLine,
  replaceWaiterLine,
} from '@/features/waiter/waiterCartSlice';
import { useModalBackClose } from './useModalBackClose';
import type { CartLine, MenuItem, OptionChoice, OptionGroup, SelectedOption } from '@/types';

/** `line` — the cart's name tap: every unit of the line. `lastPlate` — the
 *  row's blue button: the last plate added only; on a qty > 1 line the
 *  edited unit is split off (SC-842, old-app rule « la remarque va au
 *  dernier plat ajouté »). Omitted = `line`. */
export type EditScope = 'line' | 'lastPlate';

export type OptionsTarget =
  | { mode: 'new'; itemId: string }
  | { mode: 'edit'; lineId: string; scope?: EditScope };

export interface TicketOptionsModalProps {
  sessionUuid: string;
  restaurantUuid: string;
  kind: 'dine_in' | 'takeaway';
  isBuffetDineIn: boolean;
  target: OptionsTarget;
  /** Annuler / ✕ / Escape / Android back — ALL mean "really cancel" (W-T-45,
   *  spec §2.4 deviation from the old app's overlay-saves bug). The scrim
   *  itself does NOT cancel — D11's centred card leaves the scrim as live
   *  tap surface on a phone, and a mis-tap there must never silently
   *  discard an in-progress selection + comment with no confirm/undo. A
   *  `mode:'new'` pending line was never added to the cart, so cancelling
   *  it needs no undo; a `mode:'edit'` line is simply left as it was. */
  onCancel: () => void;
}

/** Per-option quantity bounds — mirrors `ProductModal.tsx`'s (unexported)
 *  helpers of the same name, duplicated rather than imported: that modal
 *  renders the GUEST product picker, out of scope for this feature (same
 *  "duplicate, don't reach into an unrelated screen" call as
 *  `waiterCartSlice.ts`'s own `waiterLineMergeKey`). */
function optionMinQty(opt: OptionChoice): number {
  return Math.max(1, opt.minQty ?? 0);
}
function optionMaxQty(opt: OptionChoice): number {
  return opt.maxQty != null ? opt.maxQty : Number.POSITIVE_INFINITY;
}
function maxAllowed(g: OptionGroup): number {
  return g.max && g.max > 0 ? g.max : g.multiple ? Number.POSITIVE_INFINITY : 1;
}

/**
 * 04-ux-mobile-spec.md §2.4, 06-test-cases.md W-T-37..48 — two-pane options
 * sheet reached either from a required-group product's row `+` (`mode:
 * 'new'`, no line exists yet — Valider is what creates it) or from a cart
 * line's name / the row's round blue button (`mode:'edit'`, prefilled).
 *
 * **Deviation, pinned by the design doc's D5 semantics note**: unlike
 * `ProductModal.tsx` (guest picker), a required single-choice group starts
 * with NOTHING preselected — the row `+` already auto-opens this modal for
 * a required group, so a preselected first option would let a fast Valider
 * silently confirm "Petite" on every size menu without the waiter ever
 * having chosen. W-T-48 pins this override explicitly (06-test-cases.md's
 * matrix text still says "kept" from ProductModal — the design doc's §2.1
 * "Semantics of + / −" row is what actually governs this screen).
 */
export default function TicketOptionsModal({
  sessionUuid,
  restaurantUuid,
  kind,
  isBuffetDineIn,
  target,
  onCancel,
}: TicketOptionsModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartLines = useAppSelector(selectWaiterCartLines(sessionUuid));
  const { data: menu } = useGetMenuQuery(restaurantUuid, { skip: !restaurantUuid });

  const consumerMode: ConsumerMode = kind === 'takeaway' ? 'takeaway' : 'dine_in';

  // TicketPage only ever mounts this component while `optionsTarget` is
  // non-null (conditional render, same idiom as F2's placeholder) — a fresh
  // mount per open, so plain lazy `useState` initializers below already
  // pick up the right starting point; no reset-on-prop-change effect needed.
  const editingLine: CartLine | undefined =
    target.mode === 'edit' ? cartLines.find((l) => l.lineId === target.lineId) : undefined;
  const itemId = target.mode === 'new' ? target.itemId : editingLine?.itemId;
  const item: MenuItem | null = useMemo(
    () => (itemId ? ((menu?.items ?? []).find((i) => i.id === itemId) ?? null) : null),
    [menu, itemId],
  );
  const groups: OptionGroup[] = item?.optionGroups ?? [];

  const [selected, setSelected] = useState<SelectedOption[]>(
    () => editingLine?.selectedOptions ?? [],
  );
  const [comment, setComment] = useState(() => editingLine?.note ?? '');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    () => groups[0]?.id ?? null,
  );
  const activeGroupId = selectedGroupId ?? groups[0]?.id ?? null;
  const currentGroup = groups.find((g) => g.id === activeGroupId) ?? null;

  useModalBackClose('options-modal', onCancel);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  if (!item) return null;

  const optionQty = (groupId: string, optionId: string): number =>
    selected.find((s) => s.groupId === groupId)?.options.find((o) => o.id === optionId)?.qty ?? 0;
  const selectedCount = (groupId: string): number => {
    const entry = selected.find((s) => s.groupId === groupId);
    return entry ? entry.options.reduce((sum, o) => sum + o.qty, 0) : 0;
  };

  const setOptionQty = (group: OptionGroup, optionId: string, nextQty: number) => {
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id);
      const safeQty = Math.max(0, Math.floor(nextQty));
      if (!existing) {
        if (safeQty === 0) return prev;
        return [...prev, { groupId: group.id, options: [{ id: optionId, qty: safeQty }] }];
      }
      const nextOptions = existing.options.filter((o) => o.id !== optionId);
      if (safeQty > 0) nextOptions.push({ id: optionId, qty: safeQty });
      if (nextOptions.length === 0) return prev.filter((s) => s.groupId !== group.id);
      return prev.map((s) => (s.groupId === group.id ? { ...s, options: nextOptions } : s));
    });
  };

  const toggleSingleChoice = (group: OptionGroup, optionId: string) => {
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id);
      const isSel = existing?.options.some((o) => o.id === optionId);
      if (isSel && !group.required) {
        return prev.filter((s) => s.groupId !== group.id); // W-T-39 — optional deselect
      }
      if (existing) {
        return prev.map((s) =>
          s.groupId === group.id ? { ...s, options: [{ id: optionId, qty: 1 }] } : s,
        );
      }
      return [...prev, { groupId: group.id, options: [{ id: optionId, qty: 1 }] }];
    });
  };

  const missingLabels = groups
    .filter((g) => selectedCount(g.id) < minRequired(g))
    .map((g) => g.label);
  const activeGroupAtMax =
    !!currentGroup &&
    currentGroup.multiple &&
    Number.isFinite(maxAllowed(currentGroup)) &&
    selectedCount(currentGroup.id) >= maxAllowed(currentGroup);

  let errorText = '';
  if (missingLabels.length > 0) {
    errorText = t('product.requiredMissing', { labels: missingLabels.join(', ') });
  } else if (activeGroupAtMax && currentGroup) {
    errorText = t('product.maxHint', {
      n: selectedCount(currentGroup.id),
      max: maxAllowed(currentGroup),
    });
  }

  function optionNamesFor(sel: SelectedOption[]): string[] {
    const names: string[] = [];
    for (const s of sel) {
      const group = groups.find((g) => g.id === s.groupId);
      if (!group) continue;
      for (const entry of s.options) {
        const choice = group.options.find((o) => o.id === entry.id);
        if (!choice) continue;
        names.push(entry.qty > 1 ? `${entry.qty}× ${choice.label}` : choice.label);
      }
    }
    return names;
  }

  function handleSubmit() {
    if (missingLabels.length > 0 || !item) return;
    const trimmedComment = comment.trim() ? comment : undefined;
    // DF-0518 — dans l'ordre des questions du plat : c'est l'ordre du bon de cuisine.
    const ordered = inGroupOrder(selected, groups);
    const optionNames = optionNamesFor(ordered);
    if (target.mode === 'new') {
      dispatch(
        addWaiterLine({
          sessionUuid,
          line: {
            lineId: uuid(),
            itemId: item.id,
            qty: 1,
            selectedOptions: ordered,
            unitPrice: computeUnitPrice(item, groups, ordered, isBuffetDineIn, consumerMode),
            note: trimmedComment,
            snapshot: { name: item.name, photo: item.photo, code: item.code },
            optionNames,
          },
        }),
      );
    } else {
      dispatch(
        replaceWaiterLine({
          sessionUuid,
          lineId: target.lineId,
          selectedOptions: ordered,
          note: trimmedComment,
          optionNames,
          unitPrice: computeUnitPrice(item, groups, ordered, isBuffetDineIn, consumerMode),
          applyTo: target.scope ?? 'line',
          newLineId: uuid(),
        }),
      );
    }
    onCancel(); // closes the sheet — the mutation above is what "Valider" means here
  }

  // One group used to render no rail at all (`> 1`), so the sheet changed
  // shape from dish to dish. Groups are ALWAYS on the left, options always
  // on the right (2026-08-28): the waiter reads the same layout every time,
  // and a single required group still shows its ●/✓ state. A dish with no
  // group has no rail — the comment is then the whole sheet.
  const showLeftPane = groups.length > 0;
  const commentOnly = groups.length === 0;
  // « 1 sur 3 » / « × 3 » — only when the edit could touch more than one
  // plate; a qty-1 line needs no qualifier.
  const editScope: EditScope | null =
    target.mode === 'edit' && editingLine && editingLine.qty > 1 ? (target.scope ?? 'line') : null;

  return (
    <div
      data-testid="options-overlay"
      // No onClick — the scrim never dismisses (see onCancel doc above);
      // Escape / × / Annuler remain the only cancel paths.
      className="fixed inset-0 z-[1150] flex items-center justify-center p-3"
      style={{ backgroundColor: 'var(--w-scrim)' }}
    >
      <div
        data-testid="options-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="options-title"
        onClick={(e) => e.stopPropagation()}
        // `grid-cols-[minmax(0,1fr)]` n'est PAS decoratif (meme correctif que
        // `.waiter-root` dans waiter-root.css) : sans lui la colonne implicite
        // est `auto`, donc dimensionnee au max-content de son contenu. Une
        // ligne d'option un peu longue etirait la carte a 631px dans un
        // telephone de 390 — `overflow-hidden` coupait alors le « + » 248px
        // hors ecran (mesure du 2026-09-02), et le serveur ne pouvait plus
        // ajouter cette option du tout.
        // La derniere rangee etait figee a `64px` alors que son contenu en
        // demande 69 des qu'un message « A choisir : … » s'affiche, et 93 avec
        // un libelle de groupe long comme celui du Menu M9 — `overflow-hidden`
        // rognait alors le bas des boutons (25px visibles sur 48, mesure du
        // 2026-09-02). `minmax(64px,auto)` garde la hauteur de repos et laisse
        // la rangee grandir; le rail de groupes, lui, est inchange (il defile
        // au-dela de ~10 groupes sur un 844px, ~7 sur un 640px).
        className="grid grid-rows-[56px_minmax(0,1fr)_minmax(64px,auto)] grid-cols-[minmax(0,1fr)] w-full max-w-[560px] max-h-[90dvh] rounded-2xl bg-paper overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 border-b-2 border-hairline">
          <h2
            id="options-title"
            data-testid="options-title"
            className="min-w-0 flex-1 font-sans text-lg font-bold truncate"
          >
            <DishCodeTag name={item.name} code={item.code} />
            {item.name}
          </h2>
          {editScope && editingLine && (
            <span
              data-testid="options-scope"
              data-scope={editScope}
              className="ms-2 shrink-0 rounded-full border-2 border-ink px-2 py-0.5 font-mono text-sm font-bold tabular-nums"
            >
              {editScope === 'lastPlate'
                ? t('waiter.ticket.options.lastPlate', { n: editingLine.qty })
                : t('waiter.ticket.options.wholeLine', { n: editingLine.qty })}
            </span>
          )}
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onCancel}
            // Un × couleur encre de 44px se confondait avec le titre juste a
            // cote : c'est la SEULE sortie visible de la feuille (le scrim ne
            // ferme pas). Pastille rouge pleine de 48px, × blanc — meme rouge
            // que « Partir » et que la pastille du panier.
            className="w-12 h-12 shrink-0 ms-2 flex items-center justify-center rounded-full text-white font-sans text-[28px] font-bold leading-none"
            style={{ backgroundColor: 'var(--w-danger)' }}
          >
            ×
          </button>
        </div>

        {/* Flex, not a fixed grid template: the rail now shows for a single
            group too, so on a 360px phone it must be able to give the option
            pane back the ~28px it does not need. */}
        <div className="min-h-0 flex">
          {showLeftPane && (
            <nav
              role="tablist"
              aria-orientation="vertical"
              className="w-[92px] min-[380px]:w-[120px] shrink-0 overflow-y-auto waiter-scroll border-e-2 border-hairline p-1 pt-2 space-y-1"
            >
              {groups.map((g) => {
                const selectedTab = g.id === activeGroupId;
                const filled = selectedCount(g.id) >= minRequired(g);
                return (
                  <button
                    key={g.id}
                    type="button"
                    data-testid={`options-group-${g.id}`}
                    role="tab"
                    aria-selected={selectedTab}
                    onClick={() => setSelectedGroupId(g.id)}
                    // Unselected tabs carried NO outline at all — on a white
                    // pane they read as plain text, not as the tab strip the
                    // 3px ring on the selected one implies.
                    className={`w-full min-h-[56px] p-2 rounded-lg bg-paper text-start font-sans text-[16px] leading-[1.15] line-clamp-2 ${
                      selectedTab ? 'font-bold' : ''
                    }`}
                    style={{
                      boxShadow: selectedTab
                        ? 'inset 0 0 0 3px var(--ink)'
                        : 'inset 0 0 0 1px var(--hairline)',
                    }}
                  >
                    {g.label}
                    {isEffectivelyRequired(g) && (
                      <span
                        aria-hidden="true"
                        style={{ color: filled ? 'var(--w-success)' : 'var(--w-danger)' }}
                      >
                        {' '}
                        {filled ? '✓' : '●'}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          <div className="min-w-0 flex-1 min-h-0 overflow-y-auto waiter-scroll p-2 flex flex-col">
            {currentGroup && (
              <div className="mb-2">
                <h3 className="font-sans text-base mb-1">
                  {currentGroup.label}{' '}
                  {isEffectivelyRequired(currentGroup) && (
                    <span style={{ color: 'var(--w-danger)' }}>*</span>
                  )}
                  {currentGroup.multiple && Number.isFinite(maxAllowed(currentGroup)) && (
                    <span className="ms-2 font-mono text-xs text-ink-mute">
                      {t('product.maxHint', {
                        n: selectedCount(currentGroup.id),
                        max: maxAllowed(currentGroup),
                      })}
                    </span>
                  )}
                  {currentGroup.multiple && !Number.isFinite(maxAllowed(currentGroup)) && (
                    <span className="ms-2 font-mono text-xs text-ink-mute">
                      {t('product.several')}
                    </span>
                  )}
                </h3>
                <ul className="list-none p-0 m-0 space-y-1.5">
                  {currentGroup.options.map((opt) => {
                    const checked = optionQty(currentGroup.id, opt.id) > 0;
                    if (currentGroup.multiple) {
                      const currentQty = optionQty(currentGroup.id, opt.id);
                      const minQ = optionMinQty(opt);
                      const maxQ = optionMaxQty(opt);
                      const groupRemaining =
                        maxAllowed(currentGroup) - selectedCount(currentGroup.id);
                      const plusDisabled =
                        currentQty === 0
                          ? groupRemaining < minQ || minQ > maxQ
                          : groupRemaining < 1 || currentQty >= maxQ;
                      return (
                        <li
                          key={opt.id}
                          data-testid={`options-item-${opt.id}`}
                          data-selected={checked}
                          // Tuile de l'ANCIENNE app (02-old-live-mobile-
                          // measurements.md §2b : 219×118, nom sur 2 lignes,
                          // « − qty + » empiles SOUS le nom, tuile entiere
                          // cliquable, vert quand choisi) — demande de
                          // l'utilisateur le 2026-09-02, elle prime sur la
                          // deviation « meme ligne, tuiles de 56 » de la spec
                          // §2.4. Sur une carte de sushis les noms font 40
                          // caracteres : la meme ligne ne laissait que ~90px
                          // au nom, donc « (R1) Inari sushi sa… » partout.
                          className="flex flex-col gap-1 border rounded-lg p-2"
                          style={{
                            backgroundColor: checked ? 'var(--w-incart-bg)' : undefined,
                            color: checked ? 'var(--w-incart-fg)' : undefined,
                            borderColor: checked ? 'var(--w-incart-fg)' : 'var(--hairline)',
                          }}
                        >
                          {/* Le nom est un bouton, comme la ligne d'un groupe
                              a choix unique et comme la tuile de l'ancienne
                              app : les options ne different souvent que par
                              leur fin (« … saumon avocat » / « … saumon
                              cheese ») et viser un « + » de 52px au pouce,
                              ligne par ligne, est le geste le plus lent du
                              carnet. Tap = +1, exactement le « + ».
                              `font-sans` et pas `font-mono` (18/500 de la spec
                              §2.4) : le mono prend ~30% de largeur en plus et
                              ajoutait une ligne. Plus de `truncate` : un nom
                              long s'affiche en entier, seul moyen de
                              distinguer R1 de R2. */}
                          <button
                            type="button"
                            data-testid={`options-item-tap-${opt.id}`}
                            disabled={plusDisabled}
                            onClick={() =>
                              setOptionQty(
                                currentGroup,
                                opt.id,
                                currentQty === 0 ? minQ : currentQty + 1,
                              )
                            }
                            // `text-inherit` : sans lui le bouton prend le
                            // `buttontext` de l'UA, qui passe en gris quand il
                            // est disabled — une option DEJA choisie dans un
                            // groupe arrive au plafond se serait affichee
                            // grisee, comme indisponible.
                            className="min-w-0 min-h-[44px] text-start font-sans text-[18px] leading-[1.25] text-inherit break-words"
                          >
                            {opt.label}
                            {opt.priceDelta > 0 && (
                              <span className="ms-2 font-mono text-sm text-ink-mute">
                                +{formatPrice(opt.priceDelta)}
                              </span>
                            )}
                          </button>
                          {/* `justify-end` et pas `justify-center` comme
                              l'ancienne app : dans CE carnet le stepper est
                              deja en bas a droite sur la ligne de plat
                              (TicketPlats), l'ecran d'avant — le pouce ne
                              change pas de colonne entre les deux. */}
                          <div className="flex items-center justify-end gap-1">
                            {/* Same ghost-« − » fix as the Plats row: outlined
                                twin of the filled « + », same footprint. */}
                            <button
                              type="button"
                              data-testid={`options-item-minus-${opt.id}`}
                              aria-label={t('product.removeOption', { label: opt.label })}
                              disabled={currentQty === 0}
                              onClick={() =>
                                setOptionQty(
                                  currentGroup,
                                  opt.id,
                                  currentQty - 1 < minQ ? 0 : currentQty - 1,
                                )
                              }
                              className="w-[52px] h-11 border-2 border-ink rounded-lg bg-paper text-ink font-sans text-2xl font-bold leading-none disabled:opacity-40"
                            >
                              −
                            </button>
                            <span
                              data-testid={`options-item-qty-${opt.id}`}
                              className="w-10 h-11 flex items-center justify-center font-mono font-bold text-xl tabular-nums"
                            >
                              {currentQty}
                            </span>
                            <button
                              type="button"
                              data-testid={`options-item-plus-${opt.id}`}
                              aria-label={t('product.addOption', { label: opt.label })}
                              disabled={plusDisabled}
                              onClick={() =>
                                setOptionQty(
                                  currentGroup,
                                  opt.id,
                                  currentQty === 0 ? minQ : currentQty + 1,
                                )
                              }
                              className="w-[52px] h-11 rounded-lg font-sans text-2xl font-bold leading-none text-white disabled:opacity-40"
                              style={{ backgroundColor: 'var(--ink)' }}
                            >
                              +
                            </button>
                          </div>
                        </li>
                      );
                    }
                    return (
                      <li key={opt.id}>
                        <button
                          type="button"
                          data-testid={`options-item-${opt.id}`}
                          data-selected={checked}
                          role={currentGroup.required ? 'radio' : 'checkbox'}
                          aria-checked={checked}
                          onClick={() => toggleSingleChoice(currentGroup, opt.id)}
                          className="w-full min-h-[56px] grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border rounded-lg p-2 text-start"
                          style={{
                            backgroundColor: checked ? 'var(--w-incart-bg)' : undefined,
                            color: checked ? 'var(--w-incart-fg)' : undefined,
                            borderColor: checked ? 'var(--w-incart-fg)' : 'var(--hairline)',
                          }}
                        >
                          {/* Meme regle que la ligne a stepper : le nom
                              s'affiche en entier (`break-words`), il ne se
                              coupe pas a « (R1) Inari sushi sa… ». */}
                          <span className="min-w-0 font-sans text-[18px] leading-[1.25] break-words">
                            {opt.label}
                          </span>
                          <span className="font-mono text-sm">
                            {opt.priceDelta > 0
                              ? `+${formatPrice(opt.priceDelta)}`
                              : checked
                                ? '✓'
                                : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* §2.7 — a dish without groups renders no `currentGroup` block
                above: keeping `mt-auto` then pushed the comment to the
                bottom of the pane, leaving ~1200px of empty space above it.
                Only pin it to the bottom when there is a group section to
                sit under. */}
            <div className={groups.length > 0 ? 'mt-auto' : ''}>
              <label htmlFor="waiter-options-comment" className="block font-sans text-sm mb-1">
                {t('product.commentLabel')}
              </label>
              <textarea
                id="waiter-options-comment"
                data-testid="options-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={255}
                // A dish without groups is opened FOR the comment and
                // nothing else — give it the room and the caret straight
                // away instead of one more tap.
                rows={commentOnly ? 5 : 2}
                autoFocus={commentOnly}
                placeholder={t('product.commentPlaceholder')}
                className="w-full resize-none rounded-lg border-2 border-ink bg-paper px-3 py-2 font-mono text-base"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-1 px-3 border-t-2 border-hairline">
          {errorText && (
            <p
              data-testid="options-error"
              role="alert"
              className="m-0 font-mono text-xs"
              style={{ color: 'var(--w-danger)' }}
            >
              {errorText}
            </p>
          )}
          <div className="flex items-center gap-3 pb-2">
            <button
              type="button"
              data-testid="options-cancel"
              onClick={onCancel}
              className="h-12 flex-1 border-2 border-ink rounded-btn font-sans font-bold"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              data-testid="options-submit"
              disabled={missingLabels.length > 0}
              onClick={handleSubmit}
              className="h-12 flex-1 rounded-btn font-sans font-bold text-paper disabled:opacity-40"
              style={{ backgroundColor: 'var(--ink)' }}
            >
              {t('waiter.ticket.plats.validate')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
